/**
 * The ✕ in a dialog's corner (owner ask, 2026-08-10: *"add 'X' close buttons in dialogs in the
 * corner, so it can be closed there always instead of having to scroll to a 'close' button"*).
 *
 * ## Why the way out was invisible
 *
 * Every dialog in this app puts its only visible exit in the `.modal-actions` row at the BOTTOM, and
 * `.modal-content` is `max-height: 85vh; overflow-y: auto` - so on a long form (the release editor,
 * the outlet editor, the pricing picker) leaving means scrolling DOWN past everything you did not
 * want to read. Escape works and so does a backdrop click on some of them, and neither is on screen:
 * **an exit nobody can see is not an exit.**
 *
 * ## Why this is a function and not an injection, and not a component either
 *
 * The obvious move is to stamp the button in from `a11y.ts`'s `activateModal()`, beside the ARIA and
 * the scroll lock, which is where this app already puts a behavior that every dialog must have. ⚠ It
 * is wrong here for a reason specific to Lit, and the reason is worth keeping: **a node appended into
 * a Lit-rendered element can be inside a ChildPart's range.** A `ChildPart` whose binding is the last
 * thing in its parent has `endNode === null`, and clearing it walks from its start marker to the end
 * of the parent - so an injected node at the end is deleted the first time that binding changes
 * shape, which is a dialog that loses its exit only on the second render. Prepending dodges today's
 * layout and stakes correctness on where a future template happens to put its first binding.
 *
 * A `<gh-modal-close>` element would be safe and costs a shadow root, a registration and a hop for
 * one button that renders one icon - `<gh-select>`'s own doc draws this line (machinery with no
 * behavior to share). A plain function returning a template is the smallest thing that is right.
 *
 * ⚠ **So the CONTROL is one place and the CALL is twenty**, and the second half is gated:
 * `tests/a11y.test.ts` counts `class="modal"` against `modalClose(` per file, because a dialog born
 * without one looks completely normal - which is exactly how the app spent 242 rounds without any.
 *
 * ## Why it takes the close action as an argument
 *
 * ⚠ **The close ACTION is per-modal and there is no generic version of it.** Some dialogs close by
 * state on their island (`this.tpl = undefined`), the three static ones in `public/index.html` close
 * by stamping `.hidden`, and the release editor and the quickstart wizard run a cancel path that
 * frees a pending upload. A shared control that wrote `.hidden` itself would silently fail on every
 * state-rendered modal, because Lit puts the node back on the next render - the same trap the global
 * Escape handler's comment describes. Passing the action in means the call site is the one place a
 * reader looks for it, and it is the same expression the Cancel button below already uses.
 *
 * The static three get theirs from `initModalFocus()` instead: they are light DOM, they are a closed
 * list that function already enumerates for its attribute observer, and nothing Lit owns is touched.
 */
import { html, type TemplateResult } from "lit";
import { icon } from "./components/ghIcon.ts";
import { t } from "./i18n.ts";

/** The label is what a screen reader announces; the `title` is what a mouse discovers on hover. */
export function modalCloseLabel(): string {
	return t("dialog.close");
}

/**
 * The corner ✕ for a dialog. Render it as the FIRST child of `.modal-content` - it floats right, so
 * the heading beside it flows around it, and being first is what puts it on the heading's line.
 */
export function modalClose(onClose: () => void): TemplateResult {
	return html`<button type="button" class="modal-x" aria-label=${modalCloseLabel()} title=${modalCloseLabel()} @click=${onClose}>
		${icon("close")}
	</button>`;
}
